import type { Agent } from '@/types/Agents';

/**
 * CRM entitlement for an agent.
 *
 * Source of truth in Supabase is `public.agent_has_crm(agent_id)` (see
 * supabase/crm_entitlement.sql). `agents.crm_enabled` is the cached result
 * returned by `get_agents`.
 *
 * dialup-agent must call the same SQL function before exposing booking,
 * calendar, or availability-override tools. Hours and services are still
 * stored in CRM tables for every agent and baked into the prompt when CRM
 * is off. Hiding UI is not enough.
 */

export const CRM_AGENT_TABS = ['calendar'] as const;

export type CrmAgentTab = (typeof CRM_AGENT_TABS)[number];

type CrmEntitlementSource = Pick<Agent, 'crm_enabled' | 'crm_override'> & {
  subscription?: Agent['subscription'];
};

export function resolveCrmEnabled(agent: CrmEntitlementSource): boolean {
  if (typeof agent.crm_enabled === 'boolean') return agent.crm_enabled;
  if (agent.crm_override === true) return true;
  if (agent.crm_override === false) return false;

  if (typeof agent.subscription?.crm_included === 'boolean') {
    return agent.subscription.crm_included;
  }

  return false;
}

export function hasCrm(
  agent: CrmEntitlementSource | null | undefined
): boolean {
  if (!agent) return false;
  return resolveCrmEnabled(agent);
}

export function withCrmEnabled<T extends CrmEntitlementSource>(agent: T): T {
  return { ...agent, crm_enabled: resolveCrmEnabled(agent) };
}

export function isCrmAgentTab(
  tab: string | null | undefined
): tab is CrmAgentTab {
  return tab === 'calendar';
}

export function getDefaultAgentTab(
  agent: CrmEntitlementSource | null | undefined
): 'calendar' | 'conversations' {
  if (agent == null) return 'calendar';
  return hasCrm(agent) ? 'calendar' : 'conversations';
}
